import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AUDIT_TOKENS } from "../../theme/auditTokens";
import { formatConsumption, formatDistance, formatNumber } from "../../utils/format";
import { classKmTotal, orderedUnitTotals, PeriodSummary, PeriodTotals, SummaryUnitKey, TaxClassFigures, UnitTotals, VehiclePeriodSummary } from "../../utils/periodSummary";
import { AuditButton } from "../common/AuditButton";
import { EmptyValue } from "../common/EmptyValue";
import { SurfaceCard } from "../common/SurfaceCard";
import { ToolbarIconButton } from "../common/ToolbarIconButton";
import { ReasonTaxClass } from "@/constants/ReasonTaxClasses";

interface Props {
  summary: PeriodSummary;
  /** False when the period lacks a full calendar month; the split cells then show a hint instead. */
  splitAvailable: boolean;
  /** Data of the period is still coming in: the figures shown are not final yet. */
  loading: boolean;
  /** The load failed: the vehicle count is unknown, not 0. */
  unavailable?: boolean;
  /** Vehicles loaded and in the filter; the summary covers only the loaded ones. */
  vehicles: { shown: number; total: number };
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
  onExport: () => void;
  exportDisabled: boolean;
}

/** Id of the open card, referenced by the toggle in the filter row. */
export const SUMMARY_ID = "logbook-audit-period-summary";
/** Keeps the card from pushing the table off screen when many vehicles are selected. */
const MAX_TABLE_HEIGHT = "min(260px, 30vh)";

/** Kilometres of the booked entries: the split's denominator and its two relevant shares. */
const KM_COLUMNS: { key: string; pick: (km: TaxClassFigures) => number }[] = [
  { key: "kmTotal", pick: classKmTotal },
  { key: "kmFavored", pick: km => km.favored },
  { key: "kmNotFavored", pick: km => km.notFavored },
];
const SPLIT_COLUMNS: { key: string; taxClass: ReasonTaxClass }[] = [
  { key: "consumptionFavored", taxClass: "favored" },
  { key: "consumptionNotFavored", taxClass: "notFavored" },
  { key: "consumptionNone", taxClass: "none" },
];

/**
 * Per-vehicle totals over the audited period between the filters and the table: kilometres of
 * the booked entries (total, favored, not favored), the litres or kWh used and filled up, and
 * the consumption split per tax class. The card has its own Excel export of just this table.
 * It renders only while open; `SummaryToggle` in the filter row opens it.
 */
export function PeriodSummaryCard({
  summary,
  splitAvailable,
  loading,
  unavailable = false,
  vehicles,
  open,
  onClose,
  isMobile,
  onExport,
  exportDisabled,
}: Props) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const km = (value: number) => formatDistance(value, language, 1);
  const litres = (value: number) => `${formatNumber(value, language, 1)} l`;
  /** Refuelled amounts are litres, except what Geotab's charging detection reports for electric vehicles. */
  const refuelled = (value: number, unit: SummaryUnitKey | undefined) =>
    unit === "electric" ? formatConsumption(value, unit, language) : litres(value);

  const consumptionCell = (vehicle: VehiclePeriodSummary): ReactNode => {
    if (!vehicle.device.fuelType) return <EmptyValue title={t("logbookAudit.consumption.noFuelGroup")} />;
    if (vehicle.consumption == null) return <EmptyValue title={t("logbookAudit.consumption.noData")} />;
    return formatConsumption(vehicle.consumption, vehicle.device.fuelType, language);
  };
  const refuelCell = (vehicle: VehiclePeriodSummary): ReactNode => {
    if (vehicle.refuelLiters == null) return <EmptyValue title={t("logbookAudit.summary.noRefuel")} />;
    const { app, import: imported, detected } = vehicle.refuelLitersBySource;
    const unit = vehicle.device.fuelType;
    const title =
      detected > 0
        ? t("logbookAudit.summary.refuelDetectedTooltip", { amount: refuelled(detected, unit) })
        : t("logbookAudit.summary.refuelTooltip", { app: litres(app), import: litres(imported) });
    return <Tooltip title={title}><span>{refuelled(vehicle.refuelLiters, unit)}</span></Tooltip>;
  };
  const splitCell = (vehicle: VehiclePeriodSummary, taxClass: ReasonTaxClass): ReactNode => {
    if (!splitAvailable) return <EmptyValue title={t("logbookAudit.summary.splitUnavailable")} />;
    if (!vehicle.split) return <EmptyValue title={t("logbookAudit.summary.noSplit")} />;
    const value = vehicle.split.figures[taxClass];
    const amount = (figure: number) =>
      vehicle.split?.basis === "consumption" ? formatConsumption(figure, vehicle.device.fuelType, language) : refuelled(figure, vehicle.device.fuelType);
    const classKm = vehicle.km.favored + vehicle.km.notFavored + vehicle.km.none;
    const title = t(`logbookAudit.summary.splitTooltip.${vehicle.split.basis}`, {
      basis: amount(vehicle.split.basis === "consumption" ? vehicle.consumption! : vehicle.refuelLiters!),
      km: km(vehicle.km[taxClass]),
      total: km(classKm),
    });
    return <Tooltip title={title}><span>{amount(value)}</span></Tooltip>;
  };
  /** Consumption figures of the total row, one per unit ("1.234,5 l · 456,0 kWh"). */
  const totalByUnit = (totals: PeriodTotals, pick: (bucket: UnitTotals) => number | undefined): ReactNode => {
    const parts: string[] = [];
    for (const [unit, bucket] of orderedUnitTotals(totals)) {
      const value = pick(bucket);
      if (value == null) continue;
      parts.push(unit === "unknown" ? litres(value) : formatConsumption(value, unit, language));
    }
    return parts.length === 0 ? <EmptyValue /> : parts.join(" · ");
  };
  const splitTotal = (taxClass: ReasonTaxClass): ReactNode =>
    splitAvailable ? totalByUnit(summary.totals, bucket => bucket.split?.[taxClass]) : <EmptyValue title={t("logbookAudit.summary.splitUnavailable")} />;

  const header = (key: string, tooltipKey?: string) => {
    const label = t(`logbookAudit.summary.columns.${key}`);
    return tooltipKey ? <Tooltip title={t(tooltipKey)}><span>{label}</span></Tooltip> : label;
  };

  if (!open) return null;

  return (
    <SurfaceCard as="section" id={SUMMARY_ID} aria-labelledby={`${SUMMARY_ID}-title`} sx={{ mb: "8px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", p: "6px 8px 6px 14px" }}>
        <Box component="h2" id={`${SUMMARY_ID}-title`} sx={{ m: 0, flex: 1, fontSize: 13.5, fontWeight: 700, color: AUDIT_TOKENS.ink }}>
          {t("logbookAudit.summary.title")}
        </Box>
        <Box component="span" sx={{ fontSize: 12, color: AUDIT_TOKENS.muted }}>
          {loading
            ? t("logbookAudit.summary.loading")
            : unavailable
              ? "—"
              : vehicles.shown < vehicles.total
              ? t("logbookAudit.summary.vehicleCountPartial", { count: summary.vehicles.length, shown: vehicles.shown, total: vehicles.total })
              : t("logbookAudit.summary.vehicleCount", { count: summary.vehicles.length })}
        </Box>
        <AuditButton onClick={onExport} disabled={exportDisabled} aria-label={t("logbookAudit.summary.export")} sx={{ flex: "0 0 auto", py: "6px", px: "10px", fontSize: 12.5 }}>
          {t("logbookAudit.actions.export")}
        </AuditButton>
        <ToolbarIconButton label={t("logbookAudit.summary.close")} icon={<CloseIcon fontSize="small" aria-hidden />} onClick={onClose} />
      </Box>
        <Box
          aria-busy={loading}
          sx={{ px: "14px", pb: "10px", opacity: loading ? 0.55 : 1, transition: "opacity .2s", "@media (prefers-reduced-motion: reduce)": { transition: "none" } }}
        >
          {summary.vehicles.length === 0 ? (
            <Box sx={{ color: AUDIT_TOKENS.muted, fontSize: 13 }}>{loading ? t("logbookAudit.summary.loading") : unavailable ? "—" : t("logbookAudit.summary.empty")}</Box>
          ) : isMobile ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {summary.vehicles.map(vehicle => (
                <Box key={vehicle.device.id} component="article" sx={{ borderTop: `1px solid ${AUDIT_TOKENS.line}`, pt: "10px" }}>
                  <Box sx={{ fontWeight: 700, fontSize: 14, color: AUDIT_TOKENS.ink, mb: "6px" }}>{vehicle.device.name}</Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 14px" }}>
                    {KM_COLUMNS.map(({ key, pick }) => (
                      <Field key={key} label={header(key)}>{km(pick(vehicle.km))}</Field>
                    ))}
                    <Field label={header("consumption")}>{consumptionCell(vehicle)}</Field>
                    <Field label={header("refuel")}>{refuelCell(vehicle)}</Field>
                    {SPLIT_COLUMNS.map(({ key, taxClass }) => (
                      <Field key={key} label={header(key)}>{splitCell(vehicle, taxClass)}</Field>
                    ))}
                  </Box>
                </Box>
              ))}
              {!splitAvailable && <SplitHint>{t("logbookAudit.summary.splitUnavailable")}</SplitHint>}
            </Box>
          ) : (
            <Box sx={{ maxHeight: MAX_TABLE_HEIGHT, overflow: "auto", border: `1px solid ${AUDIT_TOKENS.line}`, borderRadius: `${AUDIT_TOKENS.radius.small}px` }}>
              <Box
                component="table"
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12.5,
                  fontVariantNumeric: "tabular-nums",
                  "& th, & td": { p: "6px 10px", textAlign: "right", whiteSpace: "nowrap", borderTop: `1px solid ${AUDIT_TOKENS.line}` },
                  "& thead th:first-of-type, & tbody th, & tfoot td:first-of-type": { textAlign: "left" },
                  "& thead th": { position: "sticky", top: 0, bgcolor: AUDIT_TOKENS.surface, borderTop: 0, fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: AUDIT_TOKENS.muted, fontWeight: 600 },
                  "& tfoot td": { fontWeight: 700, bgcolor: AUDIT_TOKENS.rowHover },
                }}
              >
                <thead>
                  <tr>
                    <th scope="col">{header("vehicle")}</th>
                    {KM_COLUMNS.map(({ key }) => (
                      <th key={key} scope="col">{header(key, "logbookAudit.summary.kmTooltip")}</th>
                    ))}
                    <th scope="col">{header("consumption", "logbookAudit.summary.consumptionTooltip")}</th>
                    <th scope="col">{header("refuel", "logbookAudit.summary.refuelHeaderTooltip")}</th>
                    {SPLIT_COLUMNS.map(({ key }) => (
                      <th key={key} scope="col">{header(key, "logbookAudit.summary.splitHeaderTooltip")}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {summary.vehicles.map(vehicle => (
                    <tr key={vehicle.device.id}>
                      <th scope="row" style={{ fontWeight: 600 }}>{vehicle.device.name}</th>
                      {KM_COLUMNS.map(({ key, pick }) => (
                        <td key={key}>{km(pick(vehicle.km))}</td>
                      ))}
                      <td>{consumptionCell(vehicle)}</td>
                      <td>{refuelCell(vehicle)}</td>
                      {SPLIT_COLUMNS.map(({ key, taxClass }) => (
                        <td key={key}>{splitCell(vehicle, taxClass)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>{t("logbookAudit.summary.total")}</td>
                    {KM_COLUMNS.map(({ key, pick }) => (
                      <td key={key}>{km(pick(summary.totals.km))}</td>
                    ))}
                    <td>{totalByUnit(summary.totals, bucket => bucket.consumption)}</td>
                    <td>{totalByUnit(summary.totals, bucket => bucket.refuelLiters)}</td>
                    {SPLIT_COLUMNS.map(({ key, taxClass }) => (
                      <td key={key}>{splitTotal(taxClass)}</td>
                    ))}
                  </tr>
                </tfoot>
              </Box>
            </Box>
          )}
          {!isMobile && summary.vehicles.length > 0 && !splitAvailable && (
            <SplitHint>{t("logbookAudit.summary.splitUnavailable")}</SplitHint>
          )}
        </Box>
    </SurfaceCard>
  );
}

function SplitHint({ children }: { children: ReactNode }) {
  return <Box sx={{ mt: "8px", fontSize: 12, color: AUDIT_TOKENS.muted }}>{children}</Box>;
}

function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div>
      <Box sx={{ fontSize: 10.5, letterSpacing: ".06em", textTransform: "uppercase", color: AUDIT_TOKENS.muted, fontWeight: 600, mb: "3px" }}>{label}</Box>
      <Box sx={{ fontSize: 13, fontVariantNumeric: "tabular-nums" }}>{children}</Box>
    </div>
  );
}
